import React from 'react';
import { arrayOf, func, oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../util/reactIntl';
import {
  BOOKING_CATEGORY_DAILY,
  BOOKING_CATEGORY_OVERNIGHT,
  BOOKING_CATEGORY_HOURLY,
  BOOKING_CATEGORY_MINUTE,
} from '../../util/booking';

import css from './BookingCategoryToggle.module.css';

const LABEL_MESSAGE_IDS = {
  [BOOKING_CATEGORY_DAILY]: 'BookingCategoryToggle.daily',
  [BOOKING_CATEGORY_OVERNIGHT]: 'BookingCategoryToggle.overnight',
  [BOOKING_CATEGORY_HOURLY]: 'BookingCategoryToggle.hourly',
  [BOOKING_CATEGORY_MINUTE]: 'BookingCategoryToggle.minute',
};

/**
 * RAS-2520: lets a renter switch which time-unit category they are booking in, on
 * listings that sell tiers from more than one (e.g. both perDayPrice and perHourPrice).
 *
 * Renders NOTHING at all when the listing offers fewer than two categories - the vast
 * majority of listings - so single-tier booking panels keep their exact current layout
 * with no placeholder and no reserved space.
 */
const BookingCategoryToggleComponent = props => {
  const { rootClassName, className, categories, selectedCategory, onChange, intl } = props;

  // Single-tier listings are completely unaffected: no row, no gap, no wrapper element.
  if (!categories || categories.length <= 1) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div
      className={classes}
      role="group"
      aria-label={intl.formatMessage({ id: 'BookingCategoryToggle.groupLabel' })}
    >
      {categories.map(category => {
        const messageId = LABEL_MESSAGE_IDS[category];
        if (!messageId) {
          return null;
        }

        const isActive = category === selectedCategory;

        return (
          <button
            key={category}
            type="button"
            // Not a <form> control, but this panel does live inside booking forms -
            // an explicit type keeps it from ever acting as an implicit submit.
            className={classNames(css.pill, { [css.pillActive]: isActive })}
            aria-pressed={isActive}
            onClick={() => {
              // Re-selecting the active category would otherwise clear the renter's
              // in-progress dates for no reason (the parent resets selection on change).
              if (!isActive) {
                onChange(category);
              }
            }}
          >
            {intl.formatMessage({ id: messageId })}
          </button>
        );
      })}
    </div>
  );
};

BookingCategoryToggleComponent.defaultProps = {
  rootClassName: null,
  className: null,
  categories: [],
  selectedCategory: null,
};

BookingCategoryToggleComponent.propTypes = {
  rootClassName: string,
  className: string,
  categories: arrayOf(
    oneOf([
      BOOKING_CATEGORY_DAILY,
      BOOKING_CATEGORY_OVERNIGHT,
      BOOKING_CATEGORY_HOURLY,
      BOOKING_CATEGORY_MINUTE,
    ])
  ),
  selectedCategory: oneOf([
    BOOKING_CATEGORY_DAILY,
    BOOKING_CATEGORY_OVERNIGHT,
    BOOKING_CATEGORY_HOURLY,
    BOOKING_CATEGORY_MINUTE,
    null,
  ]),
  onChange: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const BookingCategoryToggle = injectIntl(BookingCategoryToggleComponent);

export default BookingCategoryToggle;
